import { useState, useEffect } from 'react';
import { supabase } from '../supabaseClient';
import { ChevronDown, ChevronUp, Send, Check, AlertTriangle, Lock } from 'lucide-react';

const C = {
  yellow:'#F7EC71', yellowLight:'#FEFCE8', orange:'#E4AC49', orangeLight:'#FDF3DC',
  green:'#7EA594', greenLight:'#EAF2EF', cream:'#F7F6EF', charcoal:'#2F3A42',
  navy:'#445366', mid:'#7A8A96', border:'#D8DEE3', skyBlue:'#BCD9E5',
  skyBlueLight:'#EAF4F8', skyText:'#3A8FA3', white:'#FFFFFF',
  red:'#DC3545', redLight:'#FFF0F0', amber:'#E07B00', amberLight:'#FFF3E0',
  pink:'#F2B8CB', pinkLight:'#FDF0F5', pinkActive:'#C4607A',
};

const VENUE_IDENTITY = {
  'Box Jordaan':  { bg: '#EDF3FC', text: '#2563EB', border: '#BAD4F5', short: 'Jordaan' },
  'Box Plantage': { bg: '#C8E6D8', text: '#2D7A65', border: '#7EA594', short: 'Plantage' },
};



const ALLOWED_ROLES      = ['admin', 'manager', 'supervisor'];
const VENUES_WITH_SHIFTY = ['Box Jordaan', 'Box Plantage'];

function getToday() { return new Date().toISOString().split('T')[0]; }

const SHIFTY_PROMPTS = [
  { key: 'service_notes',     label: 'Service',      placeholder: 'How was the shift? Busy, quiet, notable moments?' },
  { key: 'staff_notes',       label: 'Staff',        placeholder: 'Any lates, no-shows, performance notes, conflicts?' },
  { key: 'recognition_notes', label: 'Recognition',  placeholder: 'Who went above and beyond today?' },
  { key: 'upselling_notes',   label: 'Upselling',    placeholder: 'Was there a competition? How did it go?' },
  { key: 'support_notes',     label: 'Team Support', placeholder: 'Anyone struggling or needing follow-up / training?' },
  { key: 'stock_notes',       label: 'Stock',        placeholder: "Anything 86'd, unexpectedly low, or did we run out of anything?" },
  { key: 'guest_notes',       label: 'Guests',       placeholder: 'Complaints, compliments, incidents?' },
];

function formatDate(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr + 'T00:00:00');
  return d.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' });
}
function formatTime(isoStr) {
  if (!isoStr) return '';
  return new Date(isoStr).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
}

// ─── Confirm modal ────────────────────────────────────────────────────────────
function ConfirmModal({ shift, venue, managerName, filledCount, onConfirm, onCancel, submitting }) {
  const vi = VENUE_IDENTITY[venue] || {};
  return (
    <div
      style={{ position: 'fixed', inset: 0, background: 'rgba(47,58,66,0.5)', zIndex: 1100, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1.25rem' }}
      onClick={onCancel}
    >
      <div
        style={{ background: C.white, borderRadius: '16px', padding: '1.5rem', width: '100%', maxWidth: '380px', boxShadow: '0 12px 40px rgba(0,0,0,0.2)' }}
        onClick={e => e.stopPropagation()}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.65rem', marginBottom: '1rem' }}>
          <div style={{ width: '36px', height: '36px', borderRadius: '50%', background: C.pinkLight, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <Send size={16} color={C.pinkActive} />
          </div>
          <div>
            <p style={{ margin: 0, fontSize: '0.95rem', fontWeight: 800, color: C.charcoal }}>Submit shift report?</p>
            <p style={{ margin: 0, fontSize: '0.78rem', color: C.mid }}>This cannot be undone</p>
          </div>
        </div>

        <div style={{ background: C.cream, borderRadius: '10px', padding: '0.85rem 1rem', marginBottom: '1.1rem', display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.83rem' }}>
            <span style={{ color: C.mid }}>Supervisor</span>
            <span style={{ fontWeight: 700, color: C.charcoal }}>{managerName}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.83rem' }}>
            <span style={{ color: C.mid }}>Venue</span>
            <span style={{ fontWeight: 700, color: vi.text || C.charcoal }}>{venue}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.83rem' }}>
            <span style={{ color: C.mid }}>Shift</span>
            <span style={{ fontWeight: 700, color: C.charcoal, textTransform: 'capitalize' }}>{shift}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.83rem' }}>
            <span style={{ color: C.mid }}>Fields filled</span>
            <span style={{ fontWeight: 700, color: filledCount === SHIFTY_PROMPTS.length ? C.green : filledCount === 0 ? C.red : C.amber }}>
              {filledCount} / {SHIFTY_PROMPTS.length}
            </span>
          </div>
        </div>

        {filledCount === 0 && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.65rem 0.85rem', background: C.amberLight, border: `1px solid ${C.amber}44`, borderRadius: '8px', marginBottom: '1rem' }}>
            <AlertTriangle size={14} color={C.amber} />
            <span style={{ fontSize: '0.78rem', color: C.amber, fontWeight: 600 }}>No notes filled in — are you sure?</span>
          </div>
        )}

        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <button
            onClick={onCancel}
            style={{ flex: 1, padding: '0.6rem', border: `1.5px solid ${C.border}`, borderRadius: '8px', background: C.white, color: C.mid, fontWeight: 600, fontSize: '0.88rem', cursor: 'pointer' }}
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={submitting}
            style={{ flex: 1, padding: '0.6rem', border: 'none', borderRadius: '8px', background: submitting ? C.border : C.pinkActive, color: C.white, fontWeight: 800, fontSize: '0.88rem', cursor: submitting ? 'default' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.4rem' }}
          >
            {submitting ? 'Submitting…' : <><Send size={13} /> Confirm</>}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Shift report card ────────────────────────────────────────────────────────
function ShiftyCard({ entry, defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen);
  const filled  = SHIFTY_PROMPTS.filter(p => entry[p.key]);
  const isToday = entry.date === getToday();

  return (
    <div style={{
      background: C.white, borderRadius: '12px',
      border: `1px solid ${isToday ? `${C.pinkActive}44` : C.border}`,
      overflow: 'hidden', marginBottom: '0.65rem',
      boxShadow: isToday ? `0 2px 8px ${C.pinkActive}18` : '0 1px 4px rgba(0,0,0,0.05)',
    }}>
      <button
        onClick={() => setOpen(p => !p)}
        style={{ width: '100%', display: 'flex', alignItems: 'center', padding: '0.9rem 1.1rem', background: 'none', border: 'none', cursor: 'pointer', gap: '0.75rem', textAlign: 'left' }}
      >
        <div style={{ width: '8px', height: '8px', borderRadius: '50%', flexShrink: 0, background: entry.shift === 'brunch' ? C.orange : C.navy }} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: '0.5rem', flexWrap: 'wrap' }}>
            <span style={{ fontWeight: 700, fontSize: '0.88rem', color: C.charcoal, textTransform: 'capitalize' }}>{entry.shift}</span>
            <span style={{ fontSize: '0.78rem', color: C.mid }}>— {entry.manager_name}</span>
            {isToday && (
              <span style={{ fontSize: '0.67rem', fontWeight: 700, color: C.pinkActive, background: `${C.pinkActive}18`, padding: '0.1rem 0.4rem', borderRadius: '999px' }}>Today</span>
            )}
          </div>
          <div style={{ fontSize: '0.72rem', color: C.mid, marginTop: '0.08rem' }}>
            {formatDate(entry.date)}
            {entry.submitted_at && <span style={{ marginLeft: '0.4rem' }}>· {formatTime(entry.submitted_at)}</span>}
            <span style={{ marginLeft: '0.5rem', color: filled.length === SHIFTY_PROMPTS.length ? C.green : C.mid }}>
              {filled.length}/{SHIFTY_PROMPTS.length} fields
            </span>
          </div>
        </div>
        {open ? <ChevronUp size={14} style={{ color: C.mid, flexShrink: 0 }} /> : <ChevronDown size={14} style={{ color: C.mid, flexShrink: 0 }} />}
      </button>

      {open && (
        <div style={{ borderTop: `1px solid ${C.border}`, padding: '0.85rem 1.1rem', display: 'flex', flexDirection: 'column', gap: '0.65rem' }}>
          {SHIFTY_PROMPTS.map(({ key, label }) =>
            entry[key] ? (
              <div key={key}>
                <span style={{ fontSize: '0.67rem', fontWeight: 700, color: C.pinkActive, textTransform: 'uppercase', letterSpacing: '0.04em' }}>{label}</span>
                <p style={{ margin: '0.15rem 0 0', fontSize: '0.85rem', color: C.charcoal, lineHeight: 1.55 }}>{entry[key]}</p>
              </div>
            ) : (
              <div key={key} style={{ opacity: 0.38 }}>
                <span style={{ fontSize: '0.67rem', fontWeight: 700, color: C.mid, textTransform: 'uppercase', letterSpacing: '0.04em' }}>{label}</span>
                <p style={{ margin: '0.15rem 0 0', fontSize: '0.82rem', color: C.mid, fontStyle: 'italic' }}>Not filled in</p>
              </div>
            )
          )}
        </div>
      )}
    </div>
  );
}

// ─── Submit form ──────────────────────────────────────────────────────────────
function ShiftyForm({ venue, shift, existingForShift, currentUser, isGenericUser, onSubmitted }) {
  const autoName = !isGenericUser && currentUser?.name ? currentUser.name : null;
  const [managerName, setManagerName] = useState(autoName || '');
  const [fields,      setFields]      = useState({});
  const [submitting,  setSubmitting]  = useState(false);
  const [error,       setError]       = useState('');
  const [showConfirm, setShowConfirm] = useState(false);

  const alreadySubmitted = existingForShift.length > 0;
  const filledCount      = SHIFTY_PROMPTS.filter(p => fields[p.key]?.trim()).length;
  const canSubmit        = managerName.trim().length > 0;
  const vi               = VENUE_IDENTITY[venue] || {};

  async function doSubmit() {
    setSubmitting(true);
    setError('');
    const { data, error: dbErr } = await supabase.from('shift_log').insert({
      venue,
      shift,
      date:         getToday(),
      submitted_at: new Date().toISOString(),
      submitted_by: currentUser?.id || null,
      manager_name: managerName.trim(),
      ...Object.fromEntries(
        SHIFTY_PROMPTS.map(p => [p.key, fields[p.key]?.trim() || null])
      ),
    }).select().single();
    setSubmitting(false);
    if (dbErr) { setError('Failed to submit. Try again.'); setShowConfirm(false); return; }
    if (data && onSubmitted) onSubmitted(data);
    setFields({});
    setShowConfirm(false);
  }

  return (
    <>
      {showConfirm && (
        <ConfirmModal
          shift={shift}
          venue={venue}
          managerName={managerName}
          filledCount={filledCount}
          onConfirm={doSubmit}
          onCancel={() => setShowConfirm(false)}
          submitting={submitting}
        />
      )}

      <div style={{ background: C.white, borderRadius: '12px', border: `1.5px solid ${vi.border || C.border}`, padding: '1.25rem', boxShadow: '0 1px 4px rgba(0,0,0,0.05)', marginBottom: '1rem' }}>

        {/* Venue + shift context banner */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1rem', padding: '0.55rem 0.85rem', background: vi.bg || C.cream, borderRadius: '8px', border: `1px solid ${vi.border || C.border}` }}>
          <span style={{ fontWeight: 800, fontSize: '0.88rem', color: vi.text || C.navy }}>{venue}</span>
          <span style={{ color: vi.border || C.border, fontWeight: 300 }}>·</span>
          <span style={{ fontSize: '0.82rem', color: vi.text || C.navy, textTransform: 'capitalize', fontWeight: 600 }}>{shift} report</span>
          <span style={{ color: vi.border || C.border, fontWeight: 300 }}>·</span>
          <span style={{ fontSize: '0.78rem', color: C.mid }}>{new Date().toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' })}</span>
        </div>

        {/* Already submitted warning */}
        {alreadySubmitted && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.6rem 0.85rem', background: C.amberLight, border: `1px solid ${C.amber}44`, borderRadius: '8px', marginBottom: '1rem', fontSize: '0.8rem', color: C.amber, fontWeight: 600 }}>
            <AlertTriangle size={13} />
            A {shift} report already exists for today ({existingForShift[0]?.manager_name}, {formatTime(existingForShift[0]?.submitted_at)}). Submitting again will add a duplicate.
          </div>
        )}

        {/* Supervisor name */}
        <div style={{ marginBottom: '1rem' }}>
          <label style={{ display: 'block', fontSize: '0.69rem', fontWeight: 700, color: C.mid, textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '0.35rem' }}>
            {isGenericUser ? 'Your name *' : 'Signed as'}
          </label>
          {isGenericUser ? (
            <input
              value={managerName}
              onChange={e => setManagerName(e.target.value)}
              placeholder="Enter your name to submit"
              style={{
                width: '100%', padding: '0.5rem 0.7rem',
                border: `1.5px solid ${managerName.trim() ? C.border : C.pinkActive + '88'}`,
                borderRadius: '8px', fontSize: '0.88rem', color: C.charcoal,
                background: C.white, boxSizing: 'border-box', fontFamily: 'inherit', outline: 'none',
              }}
            />
          ) : (
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.45rem', padding: '0.45rem 0.7rem', background: C.cream, borderRadius: '8px', border: `1px solid ${C.border}` }}>
              <Lock size={12} color={C.mid} />
              <span style={{ fontSize: '0.88rem', fontWeight: 600, color: C.charcoal }}>{autoName}</span>
            </div>
          )}
        </div>

        {/* Prompt fields — only show once name is present */}
        {managerName.trim() && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.85rem', marginBottom: '1rem' }}>
            {SHIFTY_PROMPTS.map(({ key, label, placeholder }) => (
              <div key={key}>
                <label style={{ display: 'block', fontSize: '0.69rem', fontWeight: 700, color: C.mid, textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '0.25rem' }}>
                  {label}
                </label>
                <textarea
                  value={fields[key] || ''}
                  onChange={e => setFields(prev => ({ ...prev, [key]: e.target.value }))}
                  placeholder={placeholder}
                  rows={2}
                  style={{ width: '100%', padding: '0.42rem 0.6rem', border: `1px solid ${C.border}`, borderRadius: '8px', fontSize: '0.84rem', resize: 'vertical', boxSizing: 'border-box', color: C.charcoal, background: fields[key] ? C.white : C.cream, fontFamily: 'inherit' }}
                />
              </div>
            ))}
          </div>
        )}

        {error && (
          <p style={{ margin: '0 0 0.65rem', fontSize: '0.8rem', color: C.red, fontWeight: 500 }}>{error}</p>
        )}

        <div style={{ display: 'flex', gap: '0.65rem', alignItems: 'center', flexWrap: 'wrap' }}>
          <button
            onClick={() => setShowConfirm(true)}
            disabled={!canSubmit}
            style={{
              display: 'flex', alignItems: 'center', gap: '0.4rem',
              padding: '0.5rem 1.4rem',
              background: canSubmit ? C.pinkActive : C.border,
              color: C.white, border: 'none', borderRadius: '8px',
              cursor: canSubmit ? 'pointer' : 'not-allowed',
              fontWeight: 700, fontSize: '0.88rem',
              boxShadow: canSubmit ? `0 2px 8px ${C.pinkActive}44` : 'none',
              transition: 'all 0.15s',
            }}
          >
            <Send size={14} />
            Submit {shift.charAt(0).toUpperCase() + shift.slice(1)} Report
          </button>
          {!canSubmit && isGenericUser && (
            <span style={{ fontSize: '0.76rem', color: C.mid }}>Enter your name to submit</span>
          )}
        </div>
      </div>
    </>
  );
}

// ─── Venue pills ──────────────────────────────────────────────────────────────
function VenuePills({ venues, active, onChange }) {
  if (venues.length <= 1) return null;
  return (
    <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap', marginBottom: '1rem' }}>
      {venues.map(v => {
        const vi = VENUE_IDENTITY[v] || {};
        const isActive = active === v;
        return (
          <button key={v} onClick={() => onChange(v)} style={{
            padding: '0.35rem 1rem', borderRadius: '999px',
            border: `2px solid ${isActive ? vi.border || C.navy : C.border}`,
            background: isActive ? vi.bg || C.navy : C.white,
            color: isActive ? vi.text || C.white : C.mid,
            fontSize: '0.82rem', cursor: 'pointer', fontWeight: isActive ? 700 : 400,
            transition: 'all 0.15s',
          }}>{vi.short || v}</button>
        );
      })}
    </div>
  );
}

// ─── Today's status bar ───────────────────────────────────────────────────────
function TodayStatus({ todayEntries, activeShift, onShiftChange }) {
  return (
    <div style={{ display: 'flex', gap: '0.4rem', marginBottom: '1rem' }}>
      {['brunch', 'dinner'].map(s => {
        const done = todayEntries.some(e => e.shift === s);
        const isActive = activeShift === s;
        return (
          <button
            key={s}
            onClick={() => onShiftChange(s)}
            style={{
              flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.4rem',
              padding: '0.55rem 0.5rem', borderRadius: '10px',
              border: `2px solid ${isActive ? C.pinkActive : done ? `${C.green}66` : C.border}`,
              background: isActive ? C.pinkActive : done ? C.greenLight : C.white,
              color: isActive ? C.white : done ? C.green : C.mid,
              fontSize: '0.82rem', fontWeight: isActive ? 700 : 600,
              cursor: 'pointer', transition: 'all 0.15s',
            }}
          >
            {done && !isActive && <Check size={13} strokeWidth={2.5} />}
            <span style={{ textTransform: 'capitalize' }}>{s}</span>
            {done && !isActive && <span style={{ fontSize: '0.68rem', fontWeight: 700 }}>done</span>}
            {!done && !isActive && <span style={{ fontSize: '0.68rem', color: C.mid }}>pending</span>}
          </button>
        );
      })}
    </div>
  );
}

// ─── Main Shifty page ─────────────────────────────────────────────────────────
export default function Shifty({ currentUser }) {
  const userRole = currentUser?.role || '';
  const isGenericUser = currentUser?.is_generic === true;

  // Gate by profile venue — no venue set means admin/floater, sees all
  const accessibleVenues = ALLOWED_ROLES.includes(userRole)
    ? VENUES_WITH_SHIFTY.filter(v => !currentUser?.venue || v === currentUser.venue)
    : [];

  const [activeVenue,  setActiveVenue]  = useState(accessibleVenues[0] || '');
  const [activeShift,  setActiveShift]  = useState('brunch');
  const [log,          setLog]          = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [submitted,    setSubmitted]    = useState(false);
  const [filterShift,  setFilterShift]  = useState('all');

  useEffect(() => {
    if (!activeVenue) return;
    setLoading(true);
    supabase.from('shift_log')
      .select('*')
      .eq('venue', activeVenue)
      .order('submitted_at', { ascending: false })
      .limit(60)
      .then(({ data }) => { setLog(data || []); setLoading(false); });
  }, [activeVenue]);

  if (!ALLOWED_ROLES.includes(userRole)) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center', color: C.mid, fontSize: '0.9rem' }}>
        Shift reports are only available to supervisors, managers and admins.
      </div>
    );
  }

  if (!activeVenue) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center', color: C.mid, fontSize: '0.9rem' }}>
        Shifty is only available for Box Jordaan and Box Plantage.
      </div>
    );
  }

  function handleSubmitted(newEntry) {
    setLog(prev => [newEntry, ...prev]);
    setSubmitted(true);
    setTimeout(() => setSubmitted(false), 3500);
  }

  const todayEntries    = log.filter(e => e.date === getToday());
  const historyEntries  = log.filter(e => e.date !== getToday());
  const filteredHistory = filterShift === 'all'
    ? historyEntries
    : historyEntries.filter(e => e.shift === filterShift);

  const existingForShift = todayEntries.filter(e => e.shift === activeShift);
  const vi = VENUE_IDENTITY[activeVenue] || {};

  return (
    <div style={{ padding: '1.25rem', maxWidth: '720px', margin: '0 auto', background: C.cream, minHeight: '100vh' }}>

      {/* Header */}
      <div style={{ marginBottom: '1.25rem' }}>
        <h2 style={{ margin: '0 0 0.18rem', color: C.charcoal, fontSize: '1.2rem', fontWeight: 700 }}>
          Shifty
          <span style={{ fontWeight: 400, fontSize: '0.78rem', color: C.mid, marginLeft: '0.55rem' }}>end of shift report</span>
        </h2>
        <p style={{ margin: 0, fontSize: '0.8rem', color: C.mid }}>
          {new Date().toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
        </p>
      </div>

      {/* Venue pills */}
      <VenuePills
        venues={accessibleVenues}
        active={activeVenue}
        onChange={v => { setActiveVenue(v); setLog([]); setLoading(true); }}
      />

      {/* Success flash */}
      {submitted && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.65rem 1rem', background: C.greenLight, border: `1px solid ${C.green}44`, borderRadius: '9px', marginBottom: '1rem', fontSize: '0.84rem', color: C.green, fontWeight: 600 }}>
          <Check size={14} /> Shift report submitted ✓
        </div>
      )}

      {/* Shift selector with done-state ticks */}
      <div style={{ marginBottom: '0.75rem' }}>
        <p style={{ margin: '0 0 0.5rem', fontSize: '0.7rem', fontWeight: 700, color: C.mid, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
          New Report — <span style={{ color: vi.text || C.navy }}>{vi.short || activeVenue}</span>
        </p>
        <TodayStatus
          todayEntries={todayEntries}
          activeShift={activeShift}
          onShiftChange={setActiveShift}
        />
      </div>

      {/* Form */}
      <ShiftyForm
        key={`${activeVenue}-${activeShift}`}
        venue={activeVenue}
        shift={activeShift}
        existingForShift={existingForShift}
        currentUser={currentUser}
        isGenericUser={isGenericUser}
        onSubmitted={handleSubmitted}
      />

      {/* Today's reports */}
      {todayEntries.length > 0 && (
        <div style={{ marginBottom: '1.5rem' }}>
          <p style={{ margin: '0 0 0.65rem', fontSize: '0.7rem', fontWeight: 700, color: C.mid, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            Today's Reports
          </p>
          {todayEntries.map(entry => (
            <ShiftyCard key={entry.id} entry={entry} defaultOpen={todayEntries.length === 1} />
          ))}
        </div>
      )}

      {/* History */}
      <div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.65rem', flexWrap: 'wrap', gap: '0.4rem' }}>
          <p style={{ margin: 0, fontSize: '0.7rem', fontWeight: 700, color: C.mid, textTransform: 'uppercase', letterSpacing: '0.05em' }}>History</p>
          <div style={{ display: 'flex', gap: '0.3rem' }}>
            {['all', 'brunch', 'dinner'].map(f => (
              <button key={f} onClick={() => setFilterShift(f)} style={{
                padding: '0.22rem 0.65rem', borderRadius: '999px',
                border: `1px solid ${filterShift === f ? C.pinkActive : C.border}`,
                background: filterShift === f ? C.pinkActive : C.white,
                color: filterShift === f ? C.white : C.mid,
                fontSize: '0.72rem', cursor: 'pointer', fontWeight: filterShift === f ? 600 : 400,
                textTransform: 'capitalize',
              }}>{f}</button>
            ))}
          </div>
        </div>

        {loading && <p style={{ color: C.mid, fontSize: '0.84rem' }}>Loading…</p>}
        {!loading && filteredHistory.length === 0 && (
          <p style={{ color: C.mid, fontSize: '0.84rem' }}>No past reports{filterShift !== 'all' ? ` for ${filterShift}` : ''}.</p>
        )}
        {filteredHistory.map(entry => (
          <ShiftyCard key={entry.id} entry={entry} />
        ))}
      </div>
    </div>
  );
}